import type { TokenUsage } from '@toggle/toggle';

import {
  getTokenUsage,
  getTokenUsageErrorCode,
} from '~/services/token-allowance/token-allowance-service';
import { useAuthStore } from '~/stores/use-auth/store/authStore';
import {
  TOKEN_ALLOWANCE_FEATURE_FLAG,
  useFlags,
} from '~/stores/use-flags/useFlags';
import { create } from '~/utils/store/createStore';

import {
  getTokenUsageAvailability,
  isMoreRecentTokenUsage,
} from './token-usage-utils';

export type TokenUsageStatus =
  | 'loading'
  | 'available'
  | 'exhausted'
  | 'unavailable'
  | 'disabled';

export type TokenUsageUpdateSource =
  | 'initial'
  | 'reconnect'
  | 'websocket'
  | 'conversation';
export type TokenUsageFetchSource = 'initial' | 'reconnect' | 'conversation';
export type TokenUsageUnavailableReason =
  | 'unknown-user'
  | 'service-unavailable';

export interface TokenUsageState {
  usage?: TokenUsage;
  status: TokenUsageStatus;
  lastUpdateSource?: TokenUsageUpdateSource;
  unavailableReason?: TokenUsageUnavailableReason;
}

export interface SetTokenUsageExhaustedParams {
  source: TokenUsageUpdateSource;
}

export interface TokenUsageActions {
  initialise: () => Promise<void>;
  fetchUsage: (source: TokenUsageFetchSource) => Promise<void>;
  applyUsage: (usage: TokenUsage, source: TokenUsageUpdateSource) => void;
  setExhausted: (params: SetTokenUsageExhaustedParams) => void;
  setUnavailable: (reason: TokenUsageUnavailableReason) => void;
}

export type TokenUsageStore = TokenUsageState & TokenUsageActions;

interface GetTokenUsageErrorStateParams {
  errorCode: ReturnType<typeof getTokenUsageErrorCode>;
  hasPreviousUsage: boolean;
  status: TokenUsageStatus;
}

let latestFetchRequestId = 0;

const isTokenUsageEnabled = () =>
  useFlags.getState().getFeatureFlagState(TOKEN_ALLOWANCE_FEATURE_FLAG);

const getUnavailableTokenUsageState = (
  unavailableReason: TokenUsageUnavailableReason,
  hasPreviousUsage: boolean,
  status: TokenUsageStatus
): Partial<TokenUsageState> =>
  hasPreviousUsage || status === 'exhausted'
    ? { unavailableReason }
    : { status: 'unavailable', unavailableReason };

const getTokenUsageErrorState = ({
  errorCode,
  hasPreviousUsage,
  status,
}: GetTokenUsageErrorStateParams): Partial<TokenUsageState> => {
  if (errorCode === 'ALLOWANCE_DISABLED') {
    return {
      usage: undefined,
      status: 'disabled',
      lastUpdateSource: undefined,
      unavailableReason: undefined,
    };
  }

  const unavailableReason =
    errorCode === 'ALLOWANCE_UNKNOWN_USER'
      ? 'unknown-user'
      : 'service-unavailable';

  return getUnavailableTokenUsageState(
    unavailableReason,
    hasPreviousUsage,
    status
  );
};

export const useTokenUsage = create<TokenUsageStore>((set, get) => ({
  usage: undefined,
  status: 'loading',
  lastUpdateSource: undefined,
  unavailableReason: undefined,
  initialise: async () => {
    await get().fetchUsage('initial');
  },
  fetchUsage: async source => {
    if (!isTokenUsageEnabled()) {
      return;
    }

    const requestId = ++latestFetchRequestId;

    if (source === 'initial') {
      set({
        usage: undefined,
        status: 'loading',
        lastUpdateSource: undefined,
        unavailableReason: undefined,
      });
    }

    const requestUserId = useAuthStore.getState().user_id;

    if (!requestUserId) {
      return;
    }

    const response = await getTokenUsage();

    if (
      !isTokenUsageEnabled() ||
      requestId !== latestFetchRequestId ||
      requestUserId !== useAuthStore.getState().user_id
    ) {
      return;
    }

    if (response.error) {
      set(
        getTokenUsageErrorState({
          errorCode: getTokenUsageErrorCode(response.error),
          hasPreviousUsage: Boolean(get().usage),
          status: get().status,
        })
      );
      return;
    }

    set({ unavailableReason: undefined });
    get().applyUsage(response.data, source);
  },
  applyUsage: (usage, source) => {
    if (!isTokenUsageEnabled() || !isMoreRecentTokenUsage(get().usage, usage)) {
      return;
    }

    set({
      usage,
      status: getTokenUsageAvailability(usage),
      lastUpdateSource: source,
      unavailableReason: undefined,
    });
  },
  setExhausted: ({ source }) => {
    if (!isTokenUsageEnabled()) {
      return;
    }

    set({
      status: 'exhausted',
      lastUpdateSource: source,
      unavailableReason: undefined,
    });
  },
  setUnavailable: unavailableReason => {
    if (!isTokenUsageEnabled()) {
      return;
    }

    set(
      getUnavailableTokenUsageState(
        unavailableReason,
        Boolean(get().usage),
        get().status
      )
    );
  },
}));
